<template>
  <div class="budget-members-limit-fields">
    <p class="mt-10">
      Member limits aim to stop time tracking at the set amount. While uncommon, technical factors such as network
      connectivity may occasionally cause a slight overrun.
    </p>

    <div class="row form-group mt-20">
      <div class="col-sm-6">
        <ValidationProvider
          v-slot="{ errors }"
          name="threshold_percentage"
          rules="between:1,100"
        >
          <div :class="{ 'has-error': errors[0] }">
            <label class="control-label optional">Notify at</label>
            <div class="input-group col-sm-12">
              <input
                class="numeric decimal form-control"
                type="text"
                inputmode="numeric"
                :value="membersLimitsState.alert.threshold_percentage"
                :disabled="disabled || !memberLimitFieldsRequired"
                @input="changeMembersLimitsField('members_limits.alert.threshold_percentage', $event.target.value)"
              />
              <span class="input-group-addon">% of limit</span>
            </div>
            <AppDialogError :errors="errors" />
          </div>
        </ValidationProvider>
      </div>

      <div class="col-sm-6 notify-project-members">
        <ValidationProvider name="notify_members">
          <label class="toggle-switch-wrapper">
            <input
              type="checkbox"
              :value="membersLimitsState.alert.notify_members"
              :checked="membersLimitsState.alert.notify_members"
              :disabled="disabled || !memberLimitFieldsRequired"
              @input="
                changeMembersLimitsField(
                  'members_limits.alert.notify_members',
                  !membersLimitsState.alert.notify_members,
                )
              "
            />
            <div class="toggle-switch"></div>
            <span class="toggle-switch-label">Notify project members</span>
          </label>
        </ValidationProvider>
      </div>
    </div>

    <ValidationObserver
      v-for="(memberLimit, index) in membersLimitsState.groups"
      :key="`${memberLimit.uuid}`"
      v-slot="{ dirty }"
      class="member-limit-card mb-20"
      tag="div"
    >
      <div class="row form-group">
        <div class="col-sm-12">
          <AppLazySelect
            :value="membersFrom(memberLimit)"
            eager
            multiple
            class="member-filter"
            resource="member"
            label="Members"
            :fetcher="fetcher"
            :min-width="300"
            :remove-clear-all-trigger="archivedView"
            :disabled="disabled"
            @input="submitSelections($event, memberLimit)"
          >
            <template #item-leading="{ item: user }">
              <AppAvatar
                v-if="user.avatar"
                :avatar="user.avatar"
                size="speck"
              />
            </template>

            <template #item-trailing="{ item: user }">
              <span v-if="user.removed">Removed</span>
            </template>
          </AppLazySelect>
        </div>
      </div>
      <div class="row form-group">
        <div :class="[isBudgetTypeHours(memberLimit.type) ? 'col-sm-5' : 'col-sm-4']">
          <ValidationProvider
            v-slot="{ errors }"
            :name="`type[${memberLimit.uuid}]`"
            :rules="isFieldsRequired"
            slim
          >
            <div
              class="form-col form-type-select"
              :class="{ 'has-error': errors[0] }"
            >
              <label class="string required control-label">
                Type
                <abbr title="required">*</abbr>
              </label>
              <AppSelect
                id="budget-attributes-type"
                :select2-options="typeSelect2Options"
                :options="BUDGET_TYPE_OPTIONS"
                :value="memberLimit.type"
                :disabled="disabled"
                @input="changeMembersLimitsField(`members_limits.groups.${index}.type`, $event)"
              />
              <AppDialogError :errors="errors" />
            </div>
          </ValidationProvider>
        </div>

        <div
          v-if="!isBudgetTypeHours(memberLimit.type)"
          class="col-sm-4"
        >
          <ValidationProvider
            v-slot="{ errors }"
            :name="`rate[${memberLimit.uuid}]`"
            :rules="isFieldsRequired"
            slim
          >
            <div
              class="form-col"
              :class="{ 'has-error': errors[0] }"
            >
              <label class="string required control-label">
                Based on
                <abbr title="required">*</abbr>
              </label>
              <AppSelect
                id="budget-attributes-rate"
                :select2-options="rateSelect2Options"
                :options="BUDGET_USE_OPTIONS"
                :value="memberLimit.rate"
                :disabled="disabled"
                @input="changeMembersLimitsField(`members_limits.groups.${index}.rate`, $event)"
              />
              <AppDialogError :errors="errors" />
            </div>
          </ValidationProvider>
        </div>
        <div
          v-if="!isBudgetTypeHours(memberLimit.type)"
          class="col-sm-4"
        >
          <ValidationProvider
            v-slot="{ errors }"
            :name="`cost[${memberLimit.uuid}]`"
            slim
            :rules="`${isFieldsRequired}|greaterThanZero|max_value:${BUDGET_LIMIT}`"
          >
            <div
              class="string required project_budget_cost"
              :class="{ 'has-error': errors[0] }"
            >
              <label class="string required control-label">
                Cost
                <abbr title="required">*</abbr>
              </label>
              <div class="input-group col-sm-12">
                <span class="input-group-addon">{{ budgetCurrency }}</span>
                <input
                  id="budget-attributes-cost"
                  class="form-control"
                  type="text"
                  placeholder="0.0"
                  :value="memberLimit.cost"
                  :disabled="disabled"
                  @input="changeMembersLimitsField(`members_limits.groups.${index}.cost`, $event.target.value)"
                />
              </div>
              <AppDialogError :errors="errors" />
            </div>
          </ValidationProvider>
        </div>
        <div
          v-if="isBudgetTypeHours(memberLimit.type)"
          class="col-sm-7"
        >
          <AppDurationInput
            with-seconds="never"
            :placeholder="formattedPlaceholder"
            label="Hours"
            required
            greater-than-zero
            :value="memberLimit.hours_display"
            :disabled="disabled"
            @input="changeMembersLimitsField(`members_limits.groups.${index}.hours_display`, $event)"
          />
        </div>
      </div>
      <BudgetRecurrenceRow
        :disabled="disabled"
        :extended-reset-options="extendedResetOptions"
        :budget-state="memberLimit"
        @change-budget-field="
          (field, value) => changeMembersLimitsField(`members_limits.groups.${index}.${field}`, value)
        "
      />
      <a
        v-if="!archivedView && (dirty || index || memberLimit.id || memberLimit.members.length)"
        class="action text-danger remove-member-limit"
        @click.prevent="removeGroup(index)"
      >
        <i class="hi hi-delete hi-14 mr-10"></i>
        Remove limit
      </a>
    </ValidationObserver>

    <div
      v-if="!archivedView"
      class="row budget-controls"
    >
      <a
        class="add-member-limit"
        @click.prevent="changeMembersLimitsField('members_limits.add')"
      >
        + Add member limit
      </a>
    </div>

    <div
      v-show="numberOfMembersNotInProject"
      id="members-to-add"
      class="dialog-warning orange members-warning-dialog"
    >
      <div>
        {{ numberOfMembersNotInProject }} {{ numberOfMembersNotInProject | pluralize('member') }} will be automatically
        added as a 'user' to the {{ project.name }} project.
      </div>
    </div>
  </div>
</template>

<script>
import {
  BUDGET_TYPE_HOURS,
  BUDGET_TYPE_OPTIONS,
  BUDGET_USE_OPTIONS,
  BUDGET_RECURRENCE_MONTHLY,
  BUDGET_RECURRENCE_OPTIONS,
} from '~/app_constants';
import { BUDGET_LIMIT } from '~/server_env';
import { difference } from 'lodash-es';
import BudgetRecurrenceRow from './BudgetRecurrenceRow.vue';
import { ProjectsResource } from '~/resources';
import Vue from 'vue';
import { mapState, mapGetters } from 'vuex';

const DEFAULT_SELECT2_OPTIONS = {
  multiple: false,
  width: '100%',
  minimumResultsForSearch: -1,
};

export default {
  name: 'BudgetMembersLimitForm',
  components: { BudgetRecurrenceRow },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    membersLimitsState: {
      type: Object,
      required: true,
    },
    organization: {
      type: Object,
      required: true,
    },
    durationFormat: {
      type: String,
      default: 'h:mm',
    },
    budgetableMembersRequest: {
      type: Function,
      default: null,
    },
    extendedResetOptions: {
      type: Boolean,
      default: false,
    },
    memberLimitFieldsRequired: {
      type: Boolean,
      default: false,
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
  },
  data: (vm) => ({
    BUDGET_TYPE_OPTIONS,
    BUDGET_USE_OPTIONS,
    BUDGET_RECURRENCE_OPTIONS,
    BUDGET_LIMIT,
    BUDGET_TYPE_HOURS,
    BUDGET_RECURRENCE_MONTHLY,
    selectedUserIds: vm.initialSelectedUserIds(),
    numberOfMembersNotInProject: 0,
  }),
  computed: {
    ...mapState(['project']),
    ...mapGetters(['archivedView']),
    budgetCurrency() {
      return this.organization.currency?.symbol || '$';
    },
    typeSelect2Options() {
      return { ...DEFAULT_SELECT2_OPTIONS, placeholder: 'Select a type', allowClear: true };
    },
    rateSelect2Options() {
      return { ...DEFAULT_SELECT2_OPTIONS, placeholder: 'Select a rate', allowClear: true };
    },
    resetsSelect2Options() {
      return { ...DEFAULT_SELECT2_OPTIONS, allowClear: false };
    },
    formattedPlaceholder() {
      return this.durationFormat === 'decimal' ? '0.00' : '0:00';
    },
    isFieldsRequired() {
      return this.memberLimitFieldsRequired ? 'required' : '';
    },
  },
  watch: {
    errors: {
      handler(newErrors) {
        this.$emit('errors-changed', newErrors);
      },
    },
  },
  methods: {
    fetcher(payload) {
      return ProjectsResource.members({ organization_id: this.organization.id }, payload, {
        abortReload: true,
      });
    },
    initialSelectedUserIds() {
      return this.membersLimitsState.groups.reduce((acc, value) => {
        acc[value.uuid] = value.members;

        return acc;
      }, {});
    },
    membersFrom({ uuid }) {
      return this.selectedUserIds[uuid] || [];
    },
    changeMembersLimitsField(field, value) {
      if (value === undefined) {
        this.$emit('change-members-limits-field', field);
      } else {
        this.$emit('change-members-limits-field', field, value);
      }
    },
    removeUsersFromGroups(added) {
      if (added.length === 0) {
        return;
      }

      Object.entries(this.selectedUserIds).forEach(([group, ids]) => {
        const diff = difference(ids, added);

        Vue.set(this.selectedUserIds, group, diff);
        this.$emit('change-members-limits-field', `members_limits.groups.${this.indexByUUID(group)}.members`, diff);
      });
    },
    setMembersNotInProject(selectedUsersIds) {
      const projectMembersIds = Array.isArray(this.project.members) ? 
        this.project.members.map((member) => member.user_id) : 
        Object.values(this.project.members).flatMap((memberId) => memberId);

      this.numberOfMembersNotInProject = 0;

      if (selectedUsersIds.length > 0) {
        selectedUsersIds.forEach((userId) => {
          const isMember = projectMembersIds.some((projectMemberId) => projectMemberId === userId);

          if (!isMember) {
            this.numberOfMembersNotInProject += 1;
          }
        });
      }
    },
    getMembersIds() {
      return this.membersLimitsState.groups.reduce((acc, value) => {
        const memberId = value.members[0];

        if (memberId !== undefined) {
          acc.push(memberId);
        }

        return acc;
      }, []);
    },
    toggleMembersWarningDialogMessage() {
      const membersIds = [];
      const selectedUserIdsKeys = Object.keys(this.selectedUserIds);

      for (const key in this.selectedUserIds) {
        if (selectedUserIdsKeys.includes(key)) {
          if (this.selectedUserIds[key] !== undefined) {
            this.selectedUserIds[key].forEach((memberId) => {
              if (memberId !== undefined) {
                membersIds.push(memberId);
              }
            });
          }
        }
      }

      this.setMembersNotInProject(membersIds);
    },
    submitSelections(selected, memberLimit) {
      const added = difference(selected, this.membersFrom(memberLimit));
      const index = this.indexByUUID(memberLimit.uuid);

      this.removeUsersFromGroups(added);
      Vue.set(this.selectedUserIds, memberLimit.uuid, selected);

      this.toggleMembersWarningDialogMessage();

      if (added.length) {
        // Used to temporarily display warning about need to update Desktop client
        this.$emit('member-added');
      }
      this.$emit('change-members-limits-field', `members_limits.groups.${index}.members`, selected);
    },
    indexByUUID(uuid) {
      return this.membersLimitsState.groups.findIndex((group) => group.uuid === uuid);
    },
    removeGroup(index) {
      const { uuid } = this.membersLimitsState.groups[index];

      Vue.delete(this.selectedUserIds, uuid);
      this.$emit('change-members-limits-field', 'members_limits.remove', index);
      this.toggleMembersWarningDialogMessage();
    },
    isBudgetTypeHours(budgetType) {
      return String(budgetType) === String(BUDGET_TYPE_HOURS);
    },
  },
};
</script>
